// 🚨 Error Boundary Component with Advanced Error Handling
// React Patterns: Error Boundaries + Error Reporting + Fallback UI

import React from 'react';
import { log } from '../../utils/log';

const CHUNK_RELOAD_KEY = 'admin-chunk-reload';

function isChunkLoadError(error) {
  const message = String(error?.message || error || '');
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function reloadOnceForChunkError() {
  try {
    const prev = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - prev < 20000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* private mode — still try one reload */
  }
  window.location.reload();
  return true;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      chunkLoadError: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      chunkLoadError: isChunkLoadError(error),
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    log.error('🚨 Error Boundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
      chunkLoadError: isChunkLoadError(error),
    });

    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return;
    }

    // In production, you would send this to error reporting service
    this.reportError(error, errorInfo);
  }

  reportError = (error, errorInfo) => {
    // Example error reporting (replace with your service)
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: 'admin_user', // Get from context/auth
    };

    // Send to error reporting service
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorReport),
    // });

    log.info('📋 Error Report:', errorReport);
  };

  handleRetry = () => {
    // Dynamic-import failures leave a rejected promise in React.lazy — remount cannot recover.
    window.location.reload();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
            </div>

            {/* Error Title */}
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Oops! Something went wrong
            </h1>

            {/* Error Description */}
            <p className="text-gray-600 mb-6">
              {this.state.chunkLoadError
                ? 'The admin UI failed to load a JavaScript file (the edge rate limit returned 503, or a deploy just replaced the files). Reload to try again.'
                : "We're sorry, but something unexpected happened. Please try again or reload the page."}
            </p>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 mb-2">
                  Error Details (Dev Mode)
                </summary>
                <div className="bg-gray-100 p-3 rounded text-xs font-mono overflow-auto">
                  <div className="text-red-600 font-semibold mb-1">
                    {this.state.error.toString()}
                  </div>
                  <div className="text-gray-700">
                    {this.state.errorInfo?.componentStack}
                  </div>
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                Reload Page
              </button>

              <button
                onClick={() => window.history.back()}
                className="w-full text-gray-600 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Go Back
              </button>
            </div>

            {/* Error ID for support */}
            {this.state.errorId && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-400">
                  Error ID: {this.state.errorId}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;